import { Component, ComponentFactory, ComponentFactoryResolver, ComponentRef, ViewChild, ViewContainerRef } from '@angular/core';
import { AccountCreatedComponent } from './account-created/account-created.component';
import { ProgressBarComponent } from './progress-bar/progress-bar.component';
import { SelectTenantComponent } from './select-tenant/select-tenant.component';
import { SignInComponent } from './sign-in/sign-in.component';
import { SignOutComponent } from './sign-out/sign-out.component';
import { environment } from 'src/environments/environment';

// Import Firebase dependencies.
import firebase from 'firebase/app';
import 'firebase/auth';
import { UserCredential, FirebaseAuth, User} from '@firebase/auth-types';
// Import GCIP/IAP module.
import * as ciap from 'gcip-iap';

const OIDC_PROVIDER_ID = 'oidc.q-identity';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent {
  @ViewChild('ciapContainer', { read: ViewContainerRef }) private container: ViewContainerRef;
  private componentRef: ComponentRef<any>;
  private progressBarTimer: any;
  private config: any;
  public link: string;
  public originalUrl: string;
  private ciapInstance: ciap.Authentication;
  public code?: string;
  public message?: string;
  public retry?: any;

  constructor(private resolver: ComponentFactoryResolver) {
    this.progressBarTimer = null;
    this.config = environment.firebaseConfig
    this.ciapInstance = new ciap.Authentication(this);
    this.ciapInstance.start();
    this.ciapInstance.getOriginalURL().then((originalUrl) => {
      this.link = `/${window.location.search}`;
      this.originalUrl = originalUrl;
    }).catch((error) => {
      this.link = `/${window.location.search}`;
      this.originalUrl = 'N/A';
    });
  }

  public getAuth(apiKey: string, tenantId: string | null): FirebaseAuth {
    let auth = null;
    if (apiKey !== this.config.apiKey) {
      throw new Error('Invalid project!');
    }
    try {
      auth = firebase.app(tenantId || undefined).auth();
      // Tenant ID should be already set on initialization below.
    } catch (e) {
      const app = firebase.initializeApp(this.config, tenantId || '[DEFAULT]');
      auth = app.auth();
      auth.tenantId = tenantId || null;
    }
    return auth;
  }

  public handleError(error) {
    console.log(error)
    this.updateError(error);
  }

  public selectTenant(
      projectConfig: {projectId: string}, tenantIds: string[]): Promise<ciap.SelectedTenantInfo> {
    const topLevelProject = `_${projectConfig.projectId}`;
    const tenants = [];
    let charCode = 'A'.charCodeAt(0);
    tenantIds.forEach((tenantId) => {
      tenants.push({
        tenantId: tenantId || topLevelProject,
        tenantDisplayName: `Company ${String.fromCharCode(charCode)}`,
      });
      charCode++;
    });
    return new Promise((resolve, reject) => {
      this.renderSelectTenant(
          tenants,
          (selectedTenantId) => {
            this.updateError(null);
            if (selectedTenantId === topLevelProject) {
              selectedTenantId = null;
            }
            resolve({
              tenantId: selectedTenantId,
              providerIds: [],
            });
          });
    });
  }

  public startSignIn(auth: FirebaseAuth, selectedTenantInfo: ciap.SelectedTenantInfo): Promise<UserCredential> {
    return auth.getRedirectResult().catch((e) => {
      console.log("There was an error with the request: ", e)
      if (e.message.indexOf("HTTP Cloud Function returned an error") !== -1 && e.message.indexOf("Unauthorized - SU-001") !== -1) {
        this.updateError({code: "SU-001", message: "<add your google email address here>" });
      }
      if (e.code.indexOf("auth/invalid-credential") !== -1 || e.message.indexOf("IdP denied access" ) !== -1) {
        this.updateError({code: "SU-001", message: "<add your email address here>"})
      }
    })
    .then(() => {
      return new Promise((resolve, reject) => {
        this.signIn(
            !!auth.tenantId,
            () => {
              this.updateError(null);
              auth.signInWithRedirect(new (firebase.auth.OAuthProvider as any)(OIDC_PROVIDER_ID))
                .catch((error) => {
                  this.updateError(error);
                });
              return false;
            },
            () => {
              this.updateError(null);
              const provider = new firebase.auth.OAuthProvider('microsoft.com');
              provider.setCustomParameters({
                tenant: '67b38e99-fa79-4e92-9a49-4f49d3464568',
              });
              auth.signInWithRedirect(provider)
                .catch((error) => {
                  console.log(error)
                  this.updateError(error);
                });
              return false;
            });
      });
    });
  }

  public completeSignOut() {
    this.signOut();
    return Promise.resolve();
  }

  public showProgressBar() {
    // Show progress bar only if it takes longer than a certain delay.
    // This prevents flicker effects when a transition is quick and a spinner
    // is shown in between.
    this.progressBarTimer = setTimeout(() => {
      this.renderProgressBar();
    }, 1);
  }

  public hideProgressBar() {
    clearTimeout(this.progressBarTimer);
    this.hideContainer();
  }

  private ngOnDestroy() {
    this.componentRef.destroy();
  }

  private updateError(error: {code?: string, message?: string, retry?: any}) {
    this.code = error && error.code;
    this.message = error && error.message;
    this.retry = error && error.retry;
  }

  private renderSelectTenant(
      tenants: Array<{tenantId: string, tenantDisplayName: string}>,
      onclick: (tenantId: string) => void) {
    this.hideContainer();
    const factory: ComponentFactory<any> = this.resolver.resolveComponentFactory(SelectTenantComponent);

    this.componentRef = this.container.createComponent(factory);

    this.componentRef.instance.tenants = tenants;
    this.componentRef.instance.onclick = onclick;
  }

  private signIn(
      saml: boolean,
      onSignInWithQIdentity: () => boolean,
      onSignInWithQAzure: () => boolean) {
    if (this.code === "SU-001") {
      var tempCode = this.code
      var tempMessage = this.message
      this.hideContainer();
      this.updateError({code: tempCode, message: tempMessage});
    } else {
      this.hideContainer();
    }
    const factory: ComponentFactory<any> = this.resolver.resolveComponentFactory(SignInComponent);
    this.componentRef = this.container.createComponent(factory);
    this.componentRef.instance.saml = saml;
    this.componentRef.instance.onSignInWithQIdentity = onSignInWithQIdentity;
    this.componentRef.instance.onSignInWithQAzure = onSignInWithQAzure;
    this.componentRef.instance.errored = (this.code != null);
    this.componentRef.instance.originalUrl = this.originalUrl;
  }

  private signOut() {
    this.hideContainer();
    const factory: ComponentFactory<any> = this.resolver.resolveComponentFactory(SignOutComponent);
    this.componentRef = this.container.createComponent(factory);
  }

  private renderProgressBar() {
    this.hideContainer();
    const factory: ComponentFactory<any> = this.resolver.resolveComponentFactory(ProgressBarComponent);
    this.componentRef = this.container.createComponent(factory);
  }

  private renderAccountCreated() {
    this.hideContainer();
    const factory: ComponentFactory<any> = this.resolver.resolveComponentFactory(AccountCreatedComponent);
    this.componentRef = this.container.createComponent(factory);
    this.componentRef.instance.originalUrl = this.originalUrl;
  }

  private hideContainer() {
    this.updateError(null);
    this.container.clear();
  }
}

